import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  signal,
  afterNextRender,
  PLATFORM_ID,
  computed,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd, RouterOutlet } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';
import { NavbarComponent } from './shared/components/navbar/navbar.component';
import { FooterComponent } from './shared/components/footer/footer.component';
import { WebglShaderBackgroundComponent } from './shared/components/webgl-shader-background/webgl-shader-background.component';
import { ParticlesBackgroundComponent } from './shared/components/particles-background/particles-background.component';
import { ScrollProgressComponent } from './shared/components/scroll-progress/scroll-progress.component';
import { CommandPaletteComponent } from './shared/components/command-palette/command-palette.component';
import { ChatWidgetComponent } from './shared/components/chat-widget/chat-widget.component';
import { CookieConsentComponent } from './shared/components/cookie-consent/cookie-consent.component';
// NOTE: CommandPaletteComponent and CookieConsentComponent are listed in imports[]
// so the template compiler knows their types, but they are ONLY used inside @defer
// blocks below — Angular's build tool will automatically split them into lazy chunks.
import { KonamiDirective } from './shared/directives/konami.directive';
import { ThemeService } from './core/services/theme.service';
import { SeoService } from './core/services/seo.service';
import { KONAMI_FADE_MS } from './shared/constants/timing';

@Component({
  selector: 'app-root',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterOutlet,
    NavbarComponent,
    FooterComponent,
    WebglShaderBackgroundComponent,
    ParticlesBackgroundComponent,
    ScrollProgressComponent,
    CommandPaletteComponent,
    ChatWidgetComponent,
    CookieConsentComponent,
    KonamiDirective,
  ],
  template: `
    <a href="#main-content" class="skip-link">Skip to main content</a>
    <div appKonami (konami)="onKonami()"></div>
    <app-scroll-progress />
    @defer (on timer(3s)) {
      <app-webgl-shader-background />
      <app-particles-background />
    } @placeholder {
      <div class="fixed inset-0 -z-10 bg-[var(--color-bg)]" aria-hidden="true"></div>
    }
    <app-navbar />
    <main id="main-content" class="relative z-10 min-h-screen">
      <router-outlet />
    </main>
    @if (!isFullscreenRoute()) {
      <app-footer />
    }
    @defer (on idle) {
      <app-command-palette />
    }
    @defer (on timer(3s); prefetch on idle) {
      <app-chat-widget />
    } @placeholder {
      <button
        class="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-emerald-500 shadow-lg"
        aria-label="Abrir chat"
      >
        <span class="text-xl text-main">&#10024;</span>
      </button>
    }
    @defer (on idle) {
      <app-cookie-consent />
    }
    @if (konamiActive()) {
      <div class="konami-toast" role="status" aria-live="polite">🎮 Konami unlocked</div>
    }
  `,
  styles: [
    `
      .skip-link {
        position: absolute;
        top: -100px;
        left: 1rem;
        z-index: 100;
        padding: 0.75rem 1.25rem;
        background: var(--color-primary, #0a0a0a);
        color: var(--color-bg, #ffffff);
        border-radius: 0.5rem;
        font-weight: 600;
        text-decoration: none;
        transition: top 0.2s ease;
      }
      .skip-link:focus {
        top: 1rem;
        outline: 2px solid var(--color-accent, #fff);
        outline-offset: 2px;
      }
      .konami-toast {
        position: fixed;
        bottom: 2rem;
        right: 2rem;
        padding: 1rem 1.5rem;
        background: color-mix(in srgb, var(--color-bg) 95%, transparent);
        border: 1px solid var(--color-border-strong);
        color: var(--color-text);
        border-radius: 0.75rem;
        font-size: 1.1rem;
        font-weight: 600;
        box-shadow:
          0 12px 40px var(--color-overlay),
          0 0 20px var(--color-ring);
        z-index: 1100;
        animation:
          konami-shake 0.6s ease-out,
          konami-fade 3s ease-out forwards;
      }
      @keyframes konami-shake {
        0%,
        100% {
          transform: translateX(0);
        }
        10% {
          transform: translateX(-6px) rotate(-1deg);
        }
        20% {
          transform: translateX(6px) rotate(1deg);
        }
        30% {
          transform: translateX(-4px) rotate(-0.5deg);
        }
        40% {
          transform: translateX(4px) rotate(0.5deg);
        }
        50% {
          transform: translateX(-2px);
        }
        60% {
          transform: translateX(2px);
        }
      }
      @keyframes konami-fade {
        0% {
          opacity: 1;
        }
        80% {
          opacity: 1;
        }
        100% {
          opacity: 0;
        }
      }
      @media (prefers-reduced-motion: reduce) {
        .konami-toast {
          animation: konami-fade 3s ease-out forwards;
        }
      }
    `,
  ],
})
export class AppComponent {
  private readonly seo: SeoService = inject(SeoService);
  private readonly theme: ThemeService = inject(ThemeService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  readonly konamiActive = signal(false);
  private konamiTimer: ReturnType<typeof setTimeout> /* clearTimeout safe */ | null = null;

  /** Routes where the footer should be hidden (fullscreen experiences) */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );
  readonly isFullscreenRoute = computed(() => /\/showcase\b/.test(this.currentUrl()));

  constructor() {
    // sdd-bypass: T4.6 — single takeUntilDestroyed (R11) — duplicate caused the operator to register twice
    afterNextRender(() => {
      this.router.events
        .pipe(
          filter((e): e is NavigationEnd => e instanceof NavigationEnd),
          takeUntilDestroyed(this.destroyRef),
        )
        .subscribe(() => {
          const main = document.querySelector<HTMLElement>('#main-content');
          if (main) {
            main.setAttribute('tabindex', '-1');
            main.focus();
          }
        });
    });

    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        this.seo.initRouteListener();
      }
    });

    this.destroyRef.onDestroy(() => {
      if (this.konamiTimer !== null) {
        clearTimeout(this.konamiTimer);
        this.konamiTimer = null;
      }
    });
  }

  public onKonami(): void {
    if (this.konamiTimer !== null) {
      clearTimeout(this.konamiTimer);
    }
    this.konamiActive.set(true);
    this.konamiTimer = setTimeout(
      /* clearTimeout safe */ () /* clearTimeout safe */ => {
        this.konamiActive.set(false);
        this.konamiTimer = null;
      },
      KONAMI_FADE_MS,
    );
  }
}
